import { LitElement, html, nothing } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { AgentStateController } from '../services/agent-state.js';
import { router, navigateTo } from '../services/router.js';
import '../components/agents/cp-agent-list.js';
import '../components/agents/cp-agent-detail.js';
import '../components/agents/cp-agent-form.js';
import type { CpAgentForm } from '../components/agents/cp-agent-form.js';
import '../components/shared/cp-page-subnav.js';

const SYSTEM_TABS = [
  { label: 'Settings', path: '/settings' },
  { label: 'Agenten', path: '/agents' },
  { label: 'Team & Skills', path: '/team' },
  { label: 'Agent-Skills', path: '/skills' },
  { label: 'Routinen', path: '/cron' },
  { label: 'Monitoring', path: '/monitoring' },
];

@customElement('cp-page-agents')
export class CpPageAgents extends LitElement {
  createRenderRoot() { return this; }

  private _agentState = new AgentStateController(this);

  @query('cp-agent-form') private _agentForm!: CpAgentForm;

  connectedCallback(): void {
    super.connectedCallback();
    router.addEventListener('route-changed', this._onRouteChanged);
    this._loadForCurrentRoute();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    router.removeEventListener('route-changed', this._onRouteChanged);
  }

  private _onRouteChanged = (): void => {
    this._loadForCurrentRoute();
    this.requestUpdate();
  };

  private _loadForCurrentRoute(): void {
    const params = router.getParams();
    if (params.id) {
      void this._agentState.loadOne(Number(params.id));
    } else {
      void this._agentState.loadAll();
    }
  }

  private _handleAgentSelected(e: CustomEvent<{ agentId: number }>): void {
    navigateTo(`/agents/${e.detail.agentId}`);
  }

  private _handleCreateAgent(): void {
    this._agentForm?.open();
  }

  private _handleAgentCreated(): void {
    void this._agentState.loadAll();
  }

  private _handleAgentUpdated(): void {
    const params = router.getParams();
    if (params.id) {
      void this._agentState.loadOne(Number(params.id));
    }
    void this._agentState.loadAll();
  }

  private _handleAgentDeleted(): void {
    navigateTo('/agents');
    void this._agentState.loadAll();
  }

  render() {
    const params = router.getParams();
    if (params.id) {
      return this._renderDetailView(Number(params.id));
    }
    return this._renderListView();
  }

  private _renderListView() {
    const { loadingState, agents, error } = this._agentState;

    return html`
      <div class="cp-page">
        <cp-page-subnav .tabs=${SYSTEM_TABS} activePath="/agents"></cp-page-subnav>
        <div class="cp-page-agents__header">
          <div class="cp-page-agents__header-row">
            <h1 class="cp-page__title">Agenten</h1>
            <div class="cp-page-agents__header-actions">
              <button class="cp-page-agents__add-btn" @click=${this._handleCreateAgent}>
                + Neuer Agent
              </button>
            </div>
          </div>
          <p class="cp-page-agents__subtitle">Deine KI-Agenten verwalten und konfigurieren</p>
        </div>

        ${loadingState === 'loading' ? this._renderLoading() : nothing}
        ${loadingState === 'error' ? this._renderError(error) : nothing}
        ${loadingState === 'loaded' || loadingState === 'idle' ? html`
          <cp-agent-list
            .agents=${agents}
            @agent-selected=${this._handleAgentSelected}
          ></cp-agent-list>
        ` : nothing}
      </div>

      <cp-agent-form @agent-created=${this._handleAgentCreated}></cp-agent-form>
    `;
  }

  private _renderDetailView(agentId: number) {
    const { activeAgent, error } = this._agentState;

    if (error) {
      return html`<div class="cp-page">${this._renderError(error)}</div>`;
    }

    if (!activeAgent || activeAgent.id !== agentId) {
      return html`
        <div class="cp-page">
          <p class="cp-page__placeholder">Lade Agent...</p>
        </div>
      `;
    }

    return html`
      <div class="cp-page">
        <cp-agent-detail
          .agent=${activeAgent}
          @agent-updated=${this._handleAgentUpdated}
          @agent-deleted=${this._handleAgentDeleted}
        ></cp-agent-detail>
      </div>
    `;
  }

  private _renderLoading() {
    return html`
      <div class="cp-page-agents__grid cp-page-agents__grid--skeleton">
        ${[1, 2, 3].map(() => html`
          <div class="cp-page-agents__skeleton">
            <div class="cp-page-agents__skeleton-avatar"></div>
            <div class="cp-page-agents__skeleton-lines">
              <div class="cp-page-agents__skeleton-line cp-page-agents__skeleton-line--name"></div>
              <div class="cp-page-agents__skeleton-line cp-page-agents__skeleton-line--sub"></div>
            </div>
          </div>
        `)}
      </div>
    `;
  }

  private _renderError(message: string | null) {
    return html`
      <div class="cp-page-agents__error">
        <p>Fehler beim Laden: ${message ?? 'Unbekannter Fehler'}</p>
        <button class="cp-page-agents__retry-btn" @click=${() => this._loadForCurrentRoute()}>
          Erneut versuchen
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-agents': CpPageAgents;
  }
}
